import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AVAILABILITY_COLUMNS } from '@app/utils/constants/inventory-table.constant';
import {
  InventoryItemFullInformation,
  LocationStock,
} from '@app/utils/interfaces/inventory-item.interface';

@Component({
  selector: 'app-product-availability-section',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, MatFormFieldModule, MatInputModule, MatTableModule],
  templateUrl: './product-availability-section.component.html',
  styleUrls: ['./product-availability-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAvailabilitySectionComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  public readonly item = input.required<InventoryItemFullInformation>();
  public readonly parentForm = input.required<FormGroup>();
  public readonly tableColumns = AVAILABILITY_COLUMNS;
  public readonly displayedColumns = this.tableColumns.map((col) => col.key);

  public availabilityForm!: FormGroup;

  private readonly locationMockData = signal<LocationStock>({
    location: 'Warehouse',
    onHand: 5,
    reserved: 5,
    incoming: 0,
    available: 0,
  });

  public currentStatus = computed(() => {
    const data = this.locationMockData();
    const available = data.onHand - data.reserved;

    if (data.onHand === data.reserved && data.reserved > 0) {
      return 'inventory.availability.committed';
    }

    if (data.onHand === 0 && data.reserved === 0 && data.incoming > 0) {
      return 'inventory.availability.incoming';
    }

    if (available > 0) {
      return 'inventory.availability.available';
    }

    return 'inventory.availability.unavailable';
  });

  public dataFormSource = computed<LocationStock[]>(() => [this.locationMockData()]);

  public ngOnInit(): void {
    this.initForm();
    this.setupValueChanges();
  }

  private initForm(): void {
    const data = this.locationMockData();

    this.availabilityForm = this.fb.group({
      onHand: [data.onHand, [Validators.required, Validators.min(0)]],
      reserved: [data.reserved, [Validators.required, Validators.min(0)]],
      incoming: [data.incoming, [Validators.required, Validators.min(0)]],
    });

    const parent = this.parentForm();

    if (!parent.contains('availabilityForm')) {
      parent.addControl('availabilityForm', this.availabilityForm);
    }
  }

  private setupValueChanges(): void {
    this.availabilityForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values) => {
        const available = (values.onHand || 0) - (values.reserved || 0);

        this.locationMockData.set({
          location: 'Warehouse',
          onHand: values.onHand || 0,
          reserved: values.reserved || 0,
          incoming: values.incoming || 0,
          available: Math.max(0, available),
        });
      });
  }
}
